import moment from "moment";
import { useQuery } from "react-query";
import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import { APP_URL } from "../config/apiConfig";
import PlanSelectionModal from "./modals/PlanSelectionModal";
import formatCurrency from "../utils/formatCurrency";
import { useApplication } from "../contexts/ApplicationContext";

const fetchSubscriptionData = async () => {
  const { data } = await axiosInstance.get("/assinatura");
  return data?.data ?? null;
};

const TrialSubscriptionGuard = ({ children }) => {
  const { user } = useApplication();
  const company = user?.Company || {};
  const isUserReady = Boolean(user && typeof user === "object");
  const isTrial = isUserReady && (!company?.planId || !company?.planCycleId);

  const { data: subscriptionData } = useQuery(
    ["trial-subscription"],
    fetchSubscriptionData,
    {
      enabled: isTrial,
      staleTime: 5 * 60 * 1000,
    }
  );

  const expirationValue =
    subscriptionData?.planExpirationDate ||
    company?.planExpirationDate ||
    company?.planExpiration ||
    null;
  const expirationMoment = expirationValue ? moment(expirationValue) : null;
  const isTrialExpired =
    isTrial &&
    Boolean(expirationMoment?.isValid()) &&
    moment().isAfter(expirationMoment);

  const availablePlans = subscriptionData?.availablePlans || [];

  const [planModalSelection, setPlanModalSelection] = useState({
    planId: null,
    cycleId: null,
  });
  const [selectedInterval, setSelectedInterval] = useState(12);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!availablePlans.length) return;
    if (planModalSelection.planId) return;
    const fallbackPlan = availablePlans[0];
    const fallbackCycle = fallbackPlan?.cycles?.[0];
    setPlanModalSelection({
      planId: fallbackPlan?.id ?? null,
      cycleId: fallbackCycle?.id ?? null,
    });
  }, [availablePlans, planModalSelection.planId]);

  const findCycleForPlan = (plan, interval) =>
    (plan?.cycles || []).find((cycle) => cycle.intervalMonths === interval);

  const availableIntervals = useMemo(() => {
    const monthsSet = new Set();
    availablePlans.forEach((plan) => {
      (plan.cycles || []).forEach((cycle) => {
        const monthValue = Number(cycle.intervalMonths);
        if (Number.isFinite(monthValue)) {
          monthsSet.add(monthValue);
        }
      });
    });
    const sorted = Array.from(monthsSet).sort((a, b) => a - b);
    return sorted.length > 0 ? sorted : [12, 3, 1];
  }, [availablePlans]);

  useEffect(() => {
    if (availableIntervals.length === 0) return;
    if (availableIntervals.includes(selectedInterval)) return;
    const preferred = availableIntervals.includes(12)
      ? 12
      : availableIntervals[availableIntervals.length - 1];
    setSelectedInterval(preferred);
  }, [availableIntervals, selectedInterval]);

  useEffect(() => {
    const plan = availablePlans.find(
      (p) => p.id === planModalSelection.planId
    );
    if (!plan) return;
    const cycle = findCycleForPlan(plan, selectedInterval);
    if (!cycle) return;
    setPlanModalSelection((prev) => ({
      ...prev,
      cycleId: cycle.id,
    }));
  }, [selectedInterval, availablePlans, planModalSelection.planId]);

  useEffect(() => {
    setModalOpen(isTrialExpired);
  }, [isTrialExpired]);

  const billingPortalUrl =
    company?.billingPortalUrl || `${APP_URL}/assinatura`;
  const actionUrls = subscriptionData?.actionUrls || {};
  const manageUrl = actionUrls.manage || billingPortalUrl;

  const openLink = (url) => () => {
    if (!url || typeof window === "undefined") return;
    window.open(url, "_blank", "noopener");
  };

  const appendParamsToUrl = (url, params = {}) => {
    if (!url || !params) return url;
    const search = new URLSearchParams(params).toString();
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${search}`;
  };

  const handleChoosePlan = () => {
    if (!planModalSelection.planId || !planModalSelection.cycleId) return;
    const checkoutUrl = appendParamsToUrl(manageUrl, {
      planId: planModalSelection.planId,
      cycleId: planModalSelection.cycleId,
    });
    openLink(checkoutUrl)();
  };

  const confirmDisabled =
    !planModalSelection.planId || !planModalSelection.cycleId;

  return (
    <>
      {children}
      <PlanSelectionModal
        isOpen={isModalOpen}
        availablePlans={availablePlans}
        planModalSelection={planModalSelection}
        setPlanModalSelection={setPlanModalSelection}
        selectedInterval={selectedInterval}
        setSelectedInterval={setSelectedInterval}
        expandedPlanId={expandedPlanId}
        setExpandedPlanId={setExpandedPlanId}
        onConfirm={handleChoosePlan}
        formatCurrency={formatCurrency}
        confirmDisabled={confirmDisabled}
        disableBackdropClick
      />
    </>
  );
};

export default TrialSubscriptionGuard;
